/**
 * KYC Completion Debugger - Utility to debug completion percentage and verification issues
 */

import merchantKycService from '../services/merchantKycService';

export const KYCCompletionDebugger = {
  /**
   * Debug completion percentage calculation
   */
  debugCompletion: (merchantId) => {
    console.group(`🔍 KYC Completion Debug - Merchant ${merchantId}`);
    
    try {
      const profile = merchantKycService.getMerchantProfile(merchantId);
      const documents = merchantKycService.getMerchantDocuments(merchantId);
      const verification = merchantKycService.getMerchantVerification(merchantId);
      
      if (!profile) {
        console.error('❌ No profile found');
        console.groupEnd();
        return;
      }

      console.log('📊 Profile Status:', profile.kycStatus);
      console.log('📄 Documents:', documents.documents.length, 'uploaded');
      console.log('🔍 Verification Results:', verification ? 'EXISTS' : 'MISSING');
      
      // Check each completion criteria
      let completed = 0;
      const total = 8;
      
      console.log('\n📋 Completion Criteria:');
      
      // Personal info (2 points)
      const personalInfo1 = profile.personalInfo.fullName && profile.personalInfo.email;
      const personalInfo2 = profile.personalInfo.phone && profile.personalInfo.dateOfBirth;
      console.log(`1. Personal Info (Basic): ${personalInfo1 ? '✅' : '❌'} (Name: ${!!profile.personalInfo.fullName}, Email: ${!!profile.personalInfo.email})`);
      console.log(`2. Personal Info (Extended): ${personalInfo2 ? '✅' : '❌'} (Phone: ${!!profile.personalInfo.phone}, DOB: ${!!profile.personalInfo.dateOfBirth})`);
      if (personalInfo1) completed++;
      if (personalInfo2) completed++;
      
      // Business info (3 points)
      const businessInfo1 = profile.businessInfo.businessName && profile.businessInfo.businessType;
      const businessInfo2 = profile.businessInfo.pan && profile.businessInfo.registeredAddress;
      const businessInfo3 = profile.businessInfo.city && profile.businessInfo.state;
      console.log(`3. Business Info (Basic): ${businessInfo1 ? '✅' : '❌'} (Name: ${!!profile.businessInfo.businessName}, Type: ${!!profile.businessInfo.businessType})`);
      console.log(`4. Business Info (Legal): ${businessInfo2 ? '✅' : '❌'} (PAN: ${!!profile.businessInfo.pan}, Address: ${!!profile.businessInfo.registeredAddress})`);
      console.log(`5. Business Info (Location): ${businessInfo3 ? '✅' : '❌'} (City: ${!!profile.businessInfo.city}, State: ${!!profile.businessInfo.state})`);
      if (businessInfo1) completed++;
      if (businessInfo2) completed++;
      if (businessInfo3) completed++;
      
      // Bank info (1 point)
      const bankInfo = profile.bankInfo.accountNumber && profile.bankInfo.ifscCode;
      console.log(`6. Bank Info: ${bankInfo ? '✅' : '❌'} (Account: ${!!profile.bankInfo.accountNumber}, IFSC: ${!!profile.bankInfo.ifscCode})`);
      if (bankInfo) completed++;
      
      // Documents (1 point)
      const requiredDocs = ['PAN_CARD', 'AADHAAR_CARD', 'BUSINESS_REGISTRATION'];
      const uploadedDocTypes = documents.documents.map(doc => doc.type);
      const hasRequiredDocs = requiredDocs.every(type => uploadedDocTypes.includes(type));
      console.log(`7. Required Documents: ${hasRequiredDocs ? '✅' : '❌'}`);
      console.log(`   - Uploaded: [${uploadedDocTypes.join(', ')}]`);
      console.log(`   - Required: [${requiredDocs.join(', ')}]`);
      if (hasRequiredDocs) completed++;
      
      // Verification (1 point)
      const hasVerificationResults = verification && verification.results;
      const isVerificationComplete = hasVerificationResults && 
        (profile.kycStatus.status === 'APPROVED' || 
         profile.kycStatus.status === 'PENDING_REVIEW' ||
         profile.kycStatus.status === 'REJECTED');
      console.log(`8. Verification Complete: ${isVerificationComplete ? '✅' : '❌'}`);
      console.log(`   - Has Results: ${hasVerificationResults ? 'YES' : 'NO'}`);
      console.log(`   - Status: ${profile.kycStatus.status}`);
      if (isVerificationComplete) completed++;
      
      const calculatedPercentage = Math.round((completed / total) * 100);
      const storedPercentage = profile.kycStatus.completionPercentage;
      
      console.log(`\n📊 Completion Summary:`);
      console.log(`   - Completed: ${completed}/${total} criteria`);
      console.log(`   - Calculated: ${calculatedPercentage}%`);
      console.log(`   - Stored: ${storedPercentage}%`);
      console.log(`   - Match: ${calculatedPercentage === storedPercentage ? '✅' : '❌'}`);
      
      // Special case for approved status
      if (profile.kycStatus.status === 'APPROVED' && hasVerificationResults) {
        console.log(`   - Override: 100% (APPROVED status)`);
      }
      
      if (verification && verification.results) {
        console.log(`\n🔍 Verification Details:`);
        console.log(`   - Status: ${verification.results.status}`);
        console.log(`   - Final Score: ${verification.results.finalScore}%`);
        console.log(`   - Timestamp: ${verification.timestamp}`);
      }
      
    } catch (error) {
      console.error('❌ Error during debug:', error);
    }
    
    console.groupEnd();
  },

  /**
   * Fix completion percentage issues
   */
  fixCompletion: (merchantId) => {
    console.log(`🔧 Fixing completion percentage for merchant ${merchantId}`);
    
    try {
      const newPercentage = merchantKycService.calculateCompletionPercentage(merchantId);
      const updatedStatus = merchantKycService.updateMerchantKycStatus(merchantId, {
        completionPercentage: newPercentage,
        lastUpdated: new Date().toISOString()
      });
      
      if (updatedStatus) {
        console.log(`✅ Updated completion percentage to ${newPercentage}%`);
        
        // Dispatch event to refresh UI
        window.dispatchEvent(new CustomEvent('kycStatusUpdated', {
          detail: { merchantId, completionPercentage: newPercentage }
        }));
        
        return newPercentage;
      } else {
        console.error('❌ Failed to update completion percentage');
        return null;
      }
    } catch (error) {
      console.error('❌ Error fixing completion:', error);
      return null;
    }
  },

  /**
   * Check if verification should be showing as complete
   */
  checkVerificationStatus: (merchantId) => {
    console.group(`🔍 Verification Status Check - Merchant ${merchantId}`);
    
    try {
      const profile = merchantKycService.getMerchantProfile(merchantId);
      const verification = merchantKycService.getMerchantVerification(merchantId);
      
      if (!profile) {
        console.error('❌ No profile found');
        console.groupEnd();
        return false;
      }
      
      const hasVerificationResults = verification && verification.results;
      const isStatusComplete = ['APPROVED', 'PENDING_REVIEW', 'REJECTED'].includes(profile.kycStatus.status);
      
      console.log('📊 Verification Status Analysis:');
      console.log(`   - Profile Status: ${profile.kycStatus.status}`);
      console.log(`   - Has Verification Results: ${hasVerificationResults ? 'YES' : 'NO'}`);
      console.log(`   - Status Indicates Complete: ${isStatusComplete ? 'YES' : 'NO'}`);
      
      if (hasVerificationResults) {
        console.log(`   - Verification Status: ${verification.results.status}`);
        console.log(`   - Final Score: ${verification.results.finalScore}%`);
      }
      
      const shouldShowComplete = hasVerificationResults && isStatusComplete;
      console.log(`   - Should Show Complete: ${shouldShowComplete ? 'YES' : 'NO'}`);
      
      console.groupEnd();
      return shouldShowComplete;
      
    } catch (error) {
      console.error('❌ Error checking verification status:', error);
      console.groupEnd();
      return false;
    }
  }
};

// Make it available globally for debugging
if (typeof window !== 'undefined') {
  window.KYCCompletionDebugger = KYCCompletionDebugger;
}

export default KYCCompletionDebugger;